import React from "react";
import styles from "../../../../styles/AdminConsole.module.css";

/**
 * CategoryBadge
 *
 * Centralises every category → CSS class mapping that was previously
 * scattered as inline ternary chains and object lookups across all three files.
 *
 * Props:
 *   category   string   — the raw category/status string from Firestore
 *   label      string   — optional display override (falls back to category)
 *   style      object   — optional inline style overrides
 *
 * Supported category values and their mappings:
 *
 *   Supplier categories:
 *     woven, knitted, non-woven, common
 *
 *   Team categories:
 *     merchandiser, quality-control, designer, logistics, branding-retail
 *
 *   Lead statuses:
 *     approved, pending, flagged, rejected
 *
 *   Order statuses:
 *     active, completed, cancelled, draft
 *
 *   QC flag statuses:
 *     approved (→ green), rejected (→ red), flagged (→ amber)
 *
 * Usage:
 *   <CategoryBadge category={user.category} />
 *   <CategoryBadge category={lead.status} label="⚠ Flagged" />
 *   <CategoryBadge category="approved" label="Approved" />
 */

const CLASS_MAP = {
  // Supplier fabric categories
  woven: "cat_woven",
  knitted: "cat_knitted",
  "non-woven": "cat_nonwoven",
  common: "cat_common",

  // Team roles
  merchandiser: "cat_merchandiser",
  "quality-control": "cat_qualitycontrol",
  designer: "cat_designer",
  logistics: "cat_logistics",
  "branding-retail": "cat_brandingretail",

  // Lead / QC flag statuses
  approved: "cat_knitted", // green
  pending: "cat_common", // muted
  flagged: "cat_nonwoven", // amber
  rejected: "cat_woven", // red

  // Order statuses
  active: "cat_knitted",
  completed: "cat_common",
  cancelled: "cat_woven",
  draft: "cat_common",
};

const LABEL_MAP = {
  woven: "Woven",
  knitted: "Knitted",
  "non-woven": "Non-Woven",
  common: "Common",
  merchandiser: "Merchandiser",
  "quality-control": "Quality Control",
  designer: "Designer",
  logistics: "Logistics",
  "branding-retail": "Branding & Retail",
  approved: "Approved",
  pending: "⏳ Pending",
  flagged: "⚠ Flagged",
  rejected: "Rejected",
  active: "Active",
  completed: "Completed",
  cancelled: "Cancelled",
  draft: "Draft",
};

const CategoryBadge = React.memo(function CategoryBadge({
  category,
  label,
  style,
}) {
  const cssKey = CLASS_MAP[category] ?? "cat_common";
  const displayLabel = label ?? LABEL_MAP[category] ?? category;

  return (
    <span className={`${styles.categoryBadge} ${styles[cssKey]}`} style={style}>
      {displayLabel}
    </span>
  );
});

export default CategoryBadge;
